import React from 'react';
import { Palette, ChevronDown } from 'lucide-react';
import { DESIGN_OPTIONS, FONT_OPTIONS, darken } from './appearance.js';

/**
 * Tiny banner thumbnail for a design option.
 * @param {{ design: string, color: string }} props
 */
function DesignThumb({ design, color }) {
  const dark = darken(color, 0.35);
  return (
    <svg viewBox="0 0 56 56" className="w-14 h-14 rounded-xl shadow-md" aria-hidden="true">
      <rect width="56" height="56" rx="12" fill="#fff" />
      {design === 'wave' && (
        <path d="M0 0H56V30C44 22 34 36 0 28Z" fill={color} />
      )}
      {design === 'classic' && <rect width="56" height="30" fill={color} />}
      {design === 'modern' && (
        <>
          <path d="M0 0H56V40L0 33Z" fill={color} />
          <circle cx="44" cy="31" r="6" fill="#B8B8B8" />
        </>
      )}
      {design === 'professional' && (
        <>
          <rect width="56" height="30" fill={color} />
          <rect y="30" width="56" height="14" fill="#B8B8B8" />
          <rect x="20" y="28" width="16" height="6" fill="#fff" />
        </>
      )}
      {design === 'gradient' && (
        <>
          <defs>
            <linearGradient id="vc-grad-thumb" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={dark} />
              <stop offset="1" stopColor="#fff" />
            </linearGradient>
          </defs>
          <rect width="56" height="56" rx="12" fill="url(#vc-grad-thumb)" />
        </>
      )}
    </svg>
  );
}

/**
 * Appearance sub-tab: design selector, accent color pill and font dropdown.
 * @param {Object} props
 * @param {import('./appearance.js').BusinessCardAppearance} props.appearance
 * @param {(patch: Object) => void} props.onChange - Receives a partial appearance patch.
 * @param {() => void} props.onRestore
 * @returns {JSX.Element}
 */
export default function AppearanceSection({ appearance, onChange, onRestore }) {
  const color = appearance.waveStart;
  return (
    <div className="panel p-5 space-y-7">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-white/90 uppercase tracking-wider">Design</div>
        <button
          type="button"
          onClick={onRestore}
          className="text-xs text-civix-purple hover:underline font-semibold"
        >
          RESTORE DEFAULT APPEARANCE
        </button>
      </div>

      <div role="radiogroup" aria-label="Card design" className="flex flex-wrap gap-2 -mt-3">
        {DESIGN_OPTIONS.map((d) => {
          const selected = appearance.design === d.id;
          return (
            <button
              key={d.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange({ design: d.id })}
              className={`w-[104px] p-2.5 rounded-xl border-2 flex flex-col items-center gap-2 transition ${
                selected
                  ? 'border-civix-purple bg-white/[0.06]'
                  : 'border-transparent hover:bg-white/[0.04]'
              }`}
            >
              <DesignThumb design={d.id} color={color} />
              <span className={`text-xs ${selected ? 'text-white font-medium' : 'text-white/70'}`}>{d.label}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <div className="text-sm font-semibold text-white/90 uppercase tracking-wider mb-2">Color</div>
          <label className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-civix-purple text-white text-xs font-bold uppercase tracking-wider shadow-md cursor-pointer hover:brightness-110 transition">
            <Palette className="w-4 h-4" />
            Color: {color.toUpperCase()}
            <input
              type="color"
              value={color}
              onChange={(e) => onChange({ waveStart: e.target.value.toUpperCase(), waveEnd: darken(e.target.value) })}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              aria-label="Card accent color"
            />
          </label>
        </div>

        <div>
          <div className="text-sm font-semibold text-white/90 uppercase tracking-wider mb-2">Font</div>
          <div className="relative">
            <select
              value={appearance.font}
              onChange={(e) => onChange({ font: e.target.value })}
              className="w-full appearance-none bg-black/30 border border-civix-purple/60 rounded-md pl-3 pr-10 py-2.5 text-sm text-white focus:outline-none focus:border-civix-cyan"
              aria-label="Card font"
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f} value={f} className="bg-[#140b22]">{f}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-white/50 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );
}
